<!-- A dialog to create new associations of content(s) to Decoder(s).

Can be used from both directions:
  - Add content(s) to Decoder(s)
  - Add Decoder(s) to content(s)

The list of contents can include:
  - RadioSpot
  - RadioSpot closingtime=True
  - TVSpotBundle

The dialog can also be used for activation of these (without passing):
  - Channel
  - TickerMessage

The UI adapts to the given content as needed, e.g. to generate fields
for editable TVSpots.

-->
<template>
  <!-- Confirmation Dialog -->
  <div>
    <v-dialog
      scrollable
      transition="dialog-bottom-transition"
      :value="show"
      :fullscreen="allowFullscreen && $vuetify.breakpoint.xsOnly"
      :width="720"
      persistent
      lazy
      @input="(val) => $emit('update:show', val)"
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout align-center>
            <v-layout
              align-center
              wrap
            >
              <div class="headline">
                {{ title }}
              </div>
              <!-- Branding Dropdown -->
              <tv-template-branding-widget
                v-if="showBrandingsDropdown"
                ref="brandingwidget"
                class="ml-auto mr-2"
                :title="lastBrandingApplied ? lastBrandingApplied.name : null"
                :overlay-options="firstBrandableTvspot.overlay_options"
                :brandings="brandings"
                :active="lastBrandingApplied !== null"
                data-cy="branding-widget"
                @applied="onBrandingApplied"
              />
            </v-layout>
            <v-btn
              class="ml-auto mr-0 my-0"
              :disabled="busy"
              flat
              icon
              data-cy="entry-creation-dialog-abort-btn"
              :title="$t('close')"
              @click="onAborted()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-divider class="mt-2" />
        <v-card-text
          v-if="showSpotOptions || contentHasOverlayOptions"
          class="px-0 pt-0"
        >
          <!-- Dynamic Overlay Fields -->
          <v-layout
            v-if="contentHasOverlayOptions"
            column
            class="px-3 pb-2"
            style="background: #f5f5f5"
          >
            <v-subheader>{{ $t('dynamicOverlayData') }}</v-subheader>
            <v-card
              v-for="bundle in bundlesWithOverlayOptions"
              :key="bundle.ident"
              class="my-2"
            >
              <v-card-text class="pt-2 pb-0">
                <div
                  class="mb-2"
                  style="font-size: 1.15em"
                >
                  <strong>{{ bundle.mainTvSpot.title }}</strong>
                </div>
                <v-layout
                  wrap
                  justify-center
                >
                  <div
                    v-for="tvspot in bundle.tvspots"
                    :key="tvspot.ident"
                    style="position: relative"
                    class="mb-1"
                  >
                    <!-- Preview (normal and HTML-templated) -->
                    <tv-template-preview
                      v-if="tvspot.editable_template && show"
                      ref="preview"
                      :data-maintvspotid="bundle.mainTvSpot.id"
                      :data-tvspotid="tvspot.id"
                      :html-overlay="tvspot.editable_template.html_overlay"
                      :background-image="
                        !tvspot.editable_template
                          .use_tvspot_video_for_background &&
                          tvspot.editable_template
                            .use_tvspot_poster_for_preview_background
                          ? tvspot.editable_template.video_poster_for_editing
                          : null
                      "
                      :background-video="
                        tvspot.editable_template.use_tvspot_video_for_background
                          ? tvspot.preview_background_url
                          : null
                      "
                      :width="tvspot.tvformat.width"
                      :height="tvspot.tvformat.height"
                      :max-width="$store.state.common.largeDialogWidth - 80"
                      :is-multiscreen="tvspot.tvformat.is_multiscreen_format"
                      :duration="tvspot.duration"
                      :fake-player-progress="
                        tvspot.editable_template.is_animated
                      "
                      :fake-fade-in-fade-out="
                        tvspot.editable_template.fade_enabled
                      "
                      :fade-in-start="tvspot.editable_template.fadein_start"
                      :fade-in-stop="tvspot.editable_template.fadein_stop"
                      :fade-out-start="tvspot.editable_template.fadeout_start"
                      :fade-out-stop="tvspot.editable_template.fadeout_stop"
                      :class="{
                        'mx-2': bundle.tvspots.length > 1,
                        'elevation-3': tvspot.editable_template.is_animated,
                      }"
                      :zoomed-initially="bundle.tvspots.length === 1"
                      show-zoom-buttons
                      :onload-callback="
                        () =>
                          $store
                            .dispatch('MAYBE_LIST_BRANDINGS')
                            .then(() =>
                              populateAllIframesWithInitialOverlayData(
                                initialBranding
                              )
                            )
                      "
                      @restart-clicked="restartAllPlayers()"
                    />
                    <mini-preview-video
                      v-else
                      :spot="tvspot"
                      :tvformat="tvspot.tvformat"
                      allow-html-preview
                      class="clickable"
                      medium
                    />
                  </div>
                </v-layout>
              </v-card-text>
              <v-card-actions class="pt-3">
                <overlay-data
                  ref="overlaydata"
                  class="grow-1"
                  validate-on-start
                  hide-flagged-groups-during-activation
                  :data-spotid="bundle.mainTvSpot.id"
                  :overlay-options="bundle.mainTvSpot.overlay_options"
                  @change-image="
                    (opts) =>
                      onEntryCreationDialogImageChangeRequested(
                        bundle.mainTvSpot,
                        opts
                      )
                  "
                  @changed="
                    () =>
                      onEntryCreationDialogOverlayDataChanged(
                        lastBrandingApplied
                      )
                  "
                />
              </v-card-actions>
            </v-card>
          </v-layout>
          <!-- Spot Options -->
          <v-layout
            v-if="showSpotOptions"
            column
            class="px-3"
          >
            <v-subheader>{{ $t('spotOptions') }}</v-subheader>
            <v-container class="pt-0">
              <!-- Closingtime Time Widget -->
              <closingtime-controls
                v-if="
                  showClosingTimeControls &&
                    contentIncludesClosingTimeRadioSpots
                "
                :minutes-before-closing.sync="minutesBeforeClosing"
                :week-days.sync="weekDays"
                :form-is-valid.sync="closingTimeFormIsValid"
              />
              <!-- Hotrotation -->
              <div
                v-if="showHotrotation"
                style="align-items: baseline"
              >
                <v-switch
                  v-model="options.hotrotation"
                  color="msr"
                  persistent-hint
                  data-cy="hotrotation-switch"
                  :hint="$t('hotrotationSwitchHint')"
                  :label="$t('hotrotation')"
                />
              </div>
              <!-- Start/Stopdate/Weekdays -->
              <div
                v-if="showDateControls"
                style="align-items: baseline"
              >
                <v-switch
                  v-model="options.expandDateControls"
                  color="msr"
                  persistent-hint
                  data-cy="timing-switch"
                  :hint="$t('timingSwitchHint')"
                  :label="$t('setTiming')"
                />
              </div>
              <v-slide-x-transition>
                <div
                  v-if="options.expandDateControls"
                  class="mt-2"
                >
                  <v-layout :wrap="$vuetify.breakpoint.mdAndDown">
                    <!-- Startdate -->
                    <div class="menu-container">
                      <v-menu
                        v-model="options.startDateMenu"
                        class="mr-2"
                        lazy
                        :min-width="mobileDatepickerMinWidth"
                        :nudge-left="40"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="startDate"
                          :label="$t('startdate')"
                          data-cy="startdate-textfield"
                          prepend-icon="event"
                          readonly
                        />
                        <v-date-picker
                          v-model="startDate"
                          color="msr"
                          no-title
                          scrollable
                          event-color="msr"
                          :events="isEventForChosenDateRange"
                          :first-day-of-week="1"
                          :locale="$t('_datepickerLocale')"
                          :allowed-dates="isAllowedStartDateWithinHardRange"
                        />
                      </v-menu>
                    </div>
                    <!-- Stopdate -->
                    <div class="menu-container">
                      <v-menu
                        v-model="options.stopDateMenu"
                        lazy
                        :min-width="mobileDatepickerMinWidth"
                        :nudge-left="40"
                        data-cy="entry-creation-dialog-stopdate-menu"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="stopDate"
                          :label="$t('toIncluding')"
                          :placeholder="stopDatePlaceHolder"
                          data-cy="stopdate-textfield"
                          prepend-icon="event"
                          readonly
                        />
                        <v-date-picker
                          v-model="stopDate"
                          color="msr"
                          no-title
                          scrollable
                          event-color="msr"
                          :events="isEventForChosenDateRange"
                          :first-day-of-week="1"
                          :locale="$t('_datepickerLocale')"
                          :allowed-dates="isAllowedStopDateWithinHardExpiry"
                        />
                      </v-menu>
                      <v-btn
                        v-if="stopDate"
                        flat
                        icon
                        class="mx-0"
                        @click="stopDate = ''"
                      >
                        <v-icon class="msr--text">
                          clear
                        </v-icon>
                      </v-btn>
                    </div>
                    <v-layout
                      v-if="showLastInitialDatesChip"
                      class="ml-3"
                    >
                      <last-initial-dates-chip @set-dates="setInitialDates" />
                    </v-layout>
                  </v-layout>
                  <hard-range-warning-list-tile
                    v-for="content in expandedContents"
                    :key="content.ident"
                    :title="content.title"
                    :hard-start="content.hard_start"
                    :hard-expire="content.hard_expire"
                    :chosen-startdate="startDate"
                    :chosen-stopdate="stopDate"
                    @invalid="onHardRangeWarningInvalidChanged"
                  />
                </div>
              </v-slide-x-transition>
              <!-- Timeframe Select -->
              <v-slide-x-transition>
                <div
                  v-if="
                    $store.getters.userCanSetTimeFrames && showTimeframeControls
                  "
                >
                  <timeframe-controls ref="timeframe" />
                </div>
              </v-slide-x-transition>
            </v-container>
          </v-layout>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <btn-ok
            data-cy="entry-creation-dialog-confirm-btn"
            round
            :loading="busy"
            :disabled="!formIsValid"
            :label="$t('confirm')"
            :title="$t('confirm')"
            @click="onConfirmed()"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Image Replacement for EditableTVSpotTemplates -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onEntryCreationDialogImageChangeConfirmed"
    />
  </div>
</template>

<script>
import {deepCopyObject, getMainTVSpotFromBundle} from '@/utils';

import {
  chooseDefaultBrandingFromBrandings,
  getFallbackBrandingFromTVSpotTemplateData,
} from '@/mixins/tvtemplateBrandings';
import entryCreationDialogOverlayData from '@/mixins/entryCreationDialogOverlayData.js';
import entryCreationUpdateDialogClosingTime from '@/mixins/entryCreationUpdateDialogClosingTime.js';
import mobileDatepickerMinWidth from '@/mixins/mobileDatepickerMinWidth.js';
import timing from '@/mixins/timing.js';

import BtnOk from '@/components/ButtonOk';
import ClosingtimeControls from '@/components/ClosingtimeControls';
import HardRangeWarningListTile from '@/components/HardRangeWarningListTile';
import ImageReplacement from '@/components/ImageReplacement';
import LastInitialDatesChip from '@/components/LastInitialDatesChip';
import MiniPreviewVideo from '@/components/MiniPreviewVideo';
import OverlayData from '@/components/OverlayData';
import TimeframeControls from '@/components/TimeframeControls';
import TvTemplateBrandingWidget from '@/components/TvTemplateBrandingWidget.vue';
import TvTemplatePreview from '@/components/TvTemplatePreview';

export default {
  components: {
    BtnOk,
    ClosingtimeControls,
    HardRangeWarningListTile,
    ImageReplacement,
    LastInitialDatesChip,
    MiniPreviewVideo,
    OverlayData,
    TimeframeControls,
    TvTemplateBrandingWidget,
    TvTemplatePreview,
  },
  mixins: [
    entryCreationUpdateDialogClosingTime,
    entryCreationDialogOverlayData,
    mobileDatepickerMinWidth,
    timing,
  ],
  props: {
    /* Whether to show the dialog. */
    show: {
      type: Boolean,
      required: true,
    },
    /* Title displayed between abort/confirm buttons. */
    title: {
      type: String,
      required: true,
    },
    /**
     * Optional list of RadioSpots|TVSpotBundles to create entries for.
     *
     * We are using this dialog without a contents list e.g. for Channel
     * and Ticker activation.
     *
     * Can be passed to generate UI on optional attributes like
     * .overlay_options
     */
    contents: {
      type: Array[Object],
      default: null,
    },
    /* Whether to show options that affect Spots (not Tickers). */
    showSpotOptions: {
      type: Boolean,
      default: true,
    },
    /* Whether to show weekday selection UI (Ticker don't have that, closingtime have their own UI). */
    showWeekdayOptions: {
      type: Boolean,
      default: true,
    },
    /* Whether fullscreen may be used based on device size. */
    allowFullscreen: {
      type: Boolean,
      default: true,
    },
    /* Whether to show a short header asking for confirmation. */
    showInfo: {
      type: Boolean,
      default: true,
    },
    /* Whether to show a switch to toggle hotrotation for new entries. */
    showHotrotation: {
      type: Boolean,
      default: true,
    },
    /* Whether to show a switch to toggle date controls for new entries. */
    showDateControls: {
      type: Boolean,
      default: true,
    },
    /* Whether to show switch to toggle timeframe controls for new entries. */
    showTimeframeControls: {
      type: Boolean,
      default: true,
    },
    /* Whether to show time and weekdays widgets when given closingtime RadioSpots. */
    showClosingTimeControls: {
      type: Boolean,
      default: true,
    },
    /* Whether the dialog should show a busy indicator and disable buttons. */
    busy: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      options: {
        expandDateControls: false,
        hotrotation: false,
        startDateMenu: false,
        stopDateMenu: false,
      },
      numberOfInvalidHardRanges: 0,
      // Note: Via mixins we are adding some more fields like:
      //  - weekDays
      //  - minutesBeforeClosing
    };
  },
  computed: {
    brandings() {
      return this.$store.state.common.brandings;
    },
    defaultBranding() {
      return chooseDefaultBrandingFromBrandings(this.brandings);
    },
    fallbackBranding() {
      return getFallbackBrandingFromTVSpotTemplateData(
        this.firstBrandableTvspot
      );
    },
    initialBranding() {
      return this.defaultBranding || this.fallbackBranding;
    },
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    firstBrandableTvspot() {
      if (!this.bundlesWithOverlayOptions.length) {
        return null;
      }
      const bundle = this.bundlesWithOverlayOptions.filter(
        (bundle) =>
          !!bundle.mainTvSpot &&
          !!bundle.mainTvSpot.editable_template &&
          bundle.mainTvSpot.editable_template.is_brandable
      )[0];
      return bundle ? bundle.mainTvSpot : null;
    },
    showBrandingsDropdown() {
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        this.bundlesWithOverlayOptions.length > 0 &&
        this.firstBrandableTvspot
      );
    },
    /** List contents with bundle.main_tvspot instead of bundle itself. */
    expandedContents() {
      if (!this.contents) {
        return [];
      }
      return this.contents.map((content) => {
        if (content.kind === 'tvspotbundle') {
          return getMainTVSpotFromBundle(content);
        }
        return content;
      });
    },
    anyContentHasHardRange() {
      return (
        this.expandedContents.filter(
          (content) => !!content.hard_start || !!content.hard_expire
        ).length > 0
      );
    },
    formIsValid() {
      let valid = this.closingTimeFormIsValid;
      if (
        this.numberOfInvalidHardRanges > 0 &&
        this.numberOfInvalidHardRanges === this.expandedContents.length
      ) {
        return false;
      }
      if (this.contentHasOverlayOptions) {
        return valid && this.isAllOverlayDataValid;
      }
      return valid;
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        // Initialize Branding dropdown, if needed.
        this.$store
          .dispatch('MAYBE_LIST_BRANDINGS')
          .then(() => this.initializeBranding());

        // Expand sections as needed when dialog is opened.
        if (this.anyContentHasHardRange) {
          this.options.expandDateControls = true;
        }

        // Update weekDays default value, if needed.
        if (
          this.showClosingTimeControls &&
          this.contentIncludesClosingTimeRadioSpots
        ) {
          this.weekDays = this.getDefaultWeekdaysForClosingTimeRadioSpot();
        }
      }
    },
  },
  methods: {
    restartAllPlayers() {
      for (const previewWidget of this.$refs.preview) {
        previewWidget.restartPlayer();
      }
    },
    initializeBranding() {
      if (this.bundlesWithOverlayOptions.length === 0) {
        return;
      }

      // Update UI widgets and (implicitly) the iframe preview.
      const brandingWidget = this.$refs.brandingwidget;
      if (brandingWidget) {
        brandingWidget.applyBranding(this.initialBranding);
      }
      // Note: We may not have a Branding-widget, but still need to
      //   apply the fallback Branding.
      this.onBrandingApplied({branding: this.initialBranding});
    },
    onBrandingApplied(context) {
      this.lastBrandingApplied = deepCopyObject(context.branding);
      this.refreshAllIframesWithOverlayData(this.lastBrandingApplied);
    },
    onHardRangeWarningInvalidChanged(invalid) {
      if (invalid) {
        this.numberOfInvalidHardRanges += 1;
      } else {
        this.numberOfInvalidHardRanges -= 1;
      }
    },
    /**
     * Reset internal data of this dialog to defaults.
     */
    reset() {
      this.options = {
        expandDateControls: false,
        hotrotation: false,
        startDateMenu: false,
        stopDateMenu: false,
      };
      this.numberOfInvalidHardRanges = 0;
      this.resetDates();
    },
    /**
     * Signal confirmation and pass additional data like timeframes/overlayData.
     */
    onConfirmed() {
      this.rememberLastInitialDates();

      const data = {
        spotIdToOverlayData: null,
        timeframeIds: null,
      };

      // Overlay Data (with branding + duration).
      if (!this.validateOverlayDataWidgets()) {
        return;
      }
      const spotIdToOverlayData = this.getSpotsOverlayData(
        this.lastBrandingApplied
      );
      const hasOverlayData = Object.keys(spotIdToOverlayData).length > 0;
      if (hasOverlayData) {
        data.spotIdToOverlayData = spotIdToOverlayData;
      }

      // Timeframes
      const timeframeControls = this.$refs.timeframe;
      if (
        timeframeControls &&
        timeframeControls.selectedTimeframes &&
        timeframeControls.selectedTimeframes.length > 0
      ) {
        data.timeframeIds = timeframeControls.selectedTimeframes.map(
          (x) => x.id
        );
      }

      this.$emit('confirmed', data);
    },
    onAborted() {
      this.$emit('aborted');
    },
  },
};
</script>

<style scoped>
.menu-container {
  display: flex;
  align-items: baseline;
}

.btn-icon-min-width {
  min-width: 36px;
}
</style>



// WEBPACK FOOTER //
// src/components/EntryCreationDialog.vue